<template>
  <nav class="unified-nav" :class="{ scrolled: isScrolled }">
    <div class="container nav-container">
      <!-- Brand -->
      <router-link :to="buildRoute('/')" class="nav-brand">
        <img :src="flavor.logos.header" :alt="flavor.displayName" class="nav-brand-logo">
      </router-link>

      <!-- Desktop nav links -->
      <ul class="nav-links d-none d-lg-flex">
        <li v-for="link in navLinks" :key="link.key">
          <router-link
            v-if="!link.authGated || authStore.isAuthenticated"
            :to="buildRoute(link.to)"
            class="nav-link-item"
            :class="{ active: isLinkActive(link) }"
          >
            {{ link.label }}
          </router-link>
          <a v-else href="#" class="nav-link-item" @click.prevent="handleAuthGatedClick">
            {{ link.label }}
          </a>
        </li>
      </ul>

      <!-- Right area -->
      <div class="nav-right-area">
        <template v-if="authStore.isAuthenticated">
          <button type="button" class="nav-icon-btn d-none d-lg-inline-flex" aria-label="Notifications">
            <i class="bi bi-bell"></i>
          </button>
          <div ref="userDropdownRef" class="user-dropdown-area">
            <button type="button" class="user-dropdown-trigger" @click.stop="toggleUserDropdown">
              <span class="user-avatar-circle">{{ userInitial }}</span>
              <span class="user-name-label d-none d-lg-inline">{{ userName }}</span>
              <i class="bi bi-caret-down-fill d-none d-lg-inline" style="font-size: 10px"></i>
            </button>
            <ul class="user-dropdown-menu" :class="{ active: isUserDropdownOpen }">
              <li class="dropdown-user-info">
                <strong>{{ userName }}</strong>
                <small>{{ authStore.session?.user?.mobile }}</small>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <router-link :to="myProfileRoute" class="dropdown-item" @click="closeUserDropdown">
                  <i class="bi bi-person-circle me-2"></i>My Profile
                </router-link>
              </li>
              <li>
                <router-link :to="myBookingsRoute" class="dropdown-item" @click="closeUserDropdown">
                  <i class="bi bi-calendar-check me-2"></i>My Bookings
                </router-link>
              </li>
              <li>
                <router-link :to="myInquiriesRoute" class="dropdown-item" @click="closeUserDropdown">
                  <i class="bi bi-inbox me-2"></i>My Inquiries
                </router-link>
              </li>
              <li v-if="canOpenManagementApp">
                <button type="button" class="dropdown-item" @click="handleGoToDashboard">
                  <i class="bi bi-speedometer2 me-2"></i>Go to Dashboard
                </button>
              </li>
              <li><hr class="dropdown-divider"></li>
              <li>
                <button type="button" class="dropdown-item" @click="handleLogout">
                  <i class="bi bi-box-arrow-right me-2"></i>Logout
                </button>
              </li>
            </ul>
          </div>
        </template>
        <template v-else>
          <button type="button" class="nav-auth-btn login d-none d-lg-inline-flex" @click="handleSignIn">Log In</button>
          <button type="button" class="nav-auth-btn signup d-none d-lg-inline-flex" @click="handleSignUp">Sign Up</button>
          <button type="button" class="mobile-login-btn d-lg-none" @click="handleSignIn" aria-label="Log in">
            <i class="bi bi-person-circle"></i>
          </button>
        </template>
        <!-- Hamburger: keeps .sidebar-button class for jQuery in custom.js -->
        <div class="sidebar-button d-lg-none" role="button" tabindex="0">
          <i class="bi bi-list"></i>
        </div>
      </div>
    </div>

    <!-- Mobile off-canvas menu (class names kept for jQuery compatibility) -->
    <div class="main-menu">
      <div class="mobile-logo-area d-lg-none d-flex align-items-center justify-content-between">
        <router-link :to="buildRoute('/')" class="mobile-logo-wrap" @click="closeMobileMenu">
          <img :src="flavor.logos.menu" :alt="flavor.displayName">
        </router-link>
        <div class="menu-close-btn"><i class="bi bi-x"></i></div>
      </div>
      <ul class="menu-list">
        <li v-for="link in mobileNavLinks" :key="link.key" :class="{ active: isLinkActive(link) }">
          <router-link
            v-if="!link.authGated || authStore.isAuthenticated"
            :to="buildRoute(link.to)"
            class="drop-down"
            @click="closeMobileMenu"
          >
            {{ link.label }}
          </router-link>
          <a v-else href="#" class="drop-down" @click.prevent="handleMobileAuthClick">
            {{ link.label }}
          </a>
        </li>
      </ul>
      <div v-if="!authStore.isAuthenticated" class="mobile-menu-auth">
        <button type="button" class="primary-btn1 black-bg d-flex menu-login-btn" @click="handleMobileSignIn">
          <span>Log In</span>
        </button>
        <button type="button" class="primary-btn1 d-flex menu-signup-btn" @click="handleMobileSignUp">
          <span>Sign Up</span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { resolveFlavor } from '@/config/flavors'
import { useAuthStore } from '@/stores/auth'
import { useAccountRoute } from '@/composables/useAccountRoute'
import { useManagementApp } from '@/composables/useManagementApp'

const router = useRouter()
const route = useRoute()
const { buildRoute } = useAccountRoute()
const flavor = inject('flavor', resolveFlavor())
const authStore = useAuthStore()

// ── Nav links ──
const navLinks = [
  { key: 'home', label: 'Home', to: '/', routeNames: ['home', 'slug-home'] },
  { key: 'listings', label: 'Listings', to: '/properties', routeNames: ['property-list', 'slug-property-list'] },
  { key: 'my-bookings', label: 'My Bookings', to: { name: 'my-bookings' }, routeNames: ['my-bookings', 'slug-my-bookings'], authGated: true },
  { key: 'my-inquiries', label: 'My Inquiries', to: { name: 'my-inquiries' }, routeNames: ['my-inquiries', 'slug-my-inquiries'], authGated: true },
  { key: 'support', label: 'Support', to: '/contact', routeNames: ['contact', 'slug-contact'] }
]

const mobileNavLinks = [
  ...navLinks,
  { key: 'about', label: 'About Us', to: '/about', routeNames: ['about', 'slug-about'] },
  { key: 'faq', label: 'FAQ', to: '/faq', routeNames: ['faq', 'slug-faq'] }
]

const isLinkActive = (link) => link.routeNames?.includes(route.name)

// ── Scroll detection for sticky shadow ──
const isScrolled = ref(false)
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10
}

// ── User dropdown ──
const isUserDropdownOpen = ref(false)
const userDropdownRef = ref(null)

const userName = computed(() => authStore.session?.user?.fullName || 'User')
const userInitial = computed(() => {
  const name = userName.value
  return name ? name.charAt(0).toUpperCase() : 'U'
})

const { canOpenManagementApp, goToManagementApp } = useManagementApp()

const toggleUserDropdown = () => {
  isUserDropdownOpen.value = !isUserDropdownOpen.value
}

const closeUserDropdown = () => {
  isUserDropdownOpen.value = false
}

const myProfileRoute = computed(() => buildRoute({ name: 'my-profile' }))
const myBookingsRoute = computed(() => buildRoute({ name: 'my-bookings' }))
const myInquiriesRoute = computed(() => buildRoute({ name: 'my-inquiries' }))

const handleGoToDashboard = () => {
  closeUserDropdown()
  goToManagementApp()
}

const handleLogout = () => {
  closeUserDropdown()
  authStore.signOut()
  router.push(buildRoute({ name: 'home' }))
}

// ── Auth (URL-driven) ──
const handleSignIn = () => {
  router.push({ query: { ...route.query, signin: 'true' } })
}

const handleSignUp = () => {
  router.push({ query: { ...route.query, signup: 'true' } })
}

const handleAuthGatedClick = () => {
  router.push({ query: { ...route.query, signin: 'true' } })
}

// ── Mobile menu ──
const closeMobileMenu = () => {
  const menu = document.querySelector('.main-menu')
  if (menu) menu.classList.remove('show-menu')
  const btn = document.querySelector('.sidebar-button')
  if (btn) btn.classList.remove('active')
}

const handleMobileAuthClick = () => {
  closeMobileMenu()
  router.push({ query: { ...route.query, signin: 'true' } })
}

const handleMobileSignIn = () => {
  closeMobileMenu()
  router.push({ query: { ...route.query, signin: 'true' } })
}

const handleMobileSignUp = () => {
  closeMobileMenu()
  router.push({ query: { ...route.query, signup: 'true' } })
}

// ── Outside-click handler ──
const handleDocumentClick = (event) => {
  const userRoot = userDropdownRef.value
  if (!userRoot || !userRoot.contains(event.target)) {
    isUserDropdownOpen.value = false
  }
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleDocumentClick)
})
</script>

<style scoped>
/* ── Unified Nav ─────────────────────────────────────────── */
.unified-nav {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #fff;
  transition: box-shadow 0.3s ease;
}

.unified-nav.scrolled {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  gap: 24px;
}

/* ── Brand ───────────────────────────────────────────────── */
.nav-brand {
  flex-shrink: 0;
}

.nav-brand-logo {
  display: block;
  width: auto;
  max-width: 120px;
  max-height: 48px;
  object-fit: contain;
}

/* ── Nav Links ───────────────────────────────────────────── */
.nav-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-link-item {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
  white-space: nowrap;
  height: 64px;
  box-sizing: border-box;
}

.nav-link-item:hover {
  color: #222;
}

.nav-link-item.active {
  color: var(--primary-color1, #0d6efd);
  border-bottom-color: var(--primary-color1, #0d6efd);
  font-weight: 600;
}

/* ── Right Area ──────────────────────────────────────────── */
.nav-right-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Bell Icon ───────────────────────────────────────────── */
.nav-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #e6e6e6;
  background: #fff;
  color: #555;
  font-size: 16px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.nav-icon-btn:hover {
  background: #f5f5f5;
  color: #222;
}

/* ── Auth Buttons ────────────────────────────────────────── */
.nav-auth-btn {
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.nav-auth-btn.login {
  background: transparent;
  border: 1px solid #e0e0e0;
  color: #333;
}

.nav-auth-btn.login:hover {
  background: #f5f5f5;
  border-color: #ccc;
}

.nav-auth-btn.signup {
  background: var(--primary-color1, #0d6efd);
  border: 1px solid var(--primary-color1, #0d6efd);
  color: #fff;
}

.nav-auth-btn.signup:hover {
  opacity: 0.9;
}

/* ── User Dropdown ───────────────────────────────────────── */
.user-dropdown-area {
  position: relative;
}

.user-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e6e6e6;
  border-radius: 40px;
  background: #fff;
  padding: 6px 14px 6px 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  color: #222;
}

.user-avatar-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--primary-color1, #0d6efd);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  flex-shrink: 0;
}

.user-name-label {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  list-style: none;
  margin: 0;
  padding: 8px 0;
  border: 1px solid #e6e6e6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  display: none;
  z-index: 100;
}

.user-dropdown-menu.active {
  display: block;
}

.dropdown-user-info {
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
}

.dropdown-user-info strong {
  font-size: 14px;
  color: #222;
}

.dropdown-user-info small {
  font-size: 12px;
  color: #888;
}

.dropdown-divider {
  margin: 4px 0;
}

.user-dropdown-menu .dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 16px;
  border: 0;
  background: none;
  font-size: 14px;
  color: #222;
  cursor: pointer;
  text-decoration: none;
}

.user-dropdown-menu .dropdown-item:hover {
  background: #f5f5f5;
}

/* ── Mobile ──────────────────────────────────────────────── */
.mobile-login-btn {
  border: 0;
  background: none;
  padding: 4px;
  cursor: pointer;
  color: #222;
  font-size: 22px;
}

.sidebar-button {
  cursor: pointer;
  font-size: 24px;
  color: #222;
  padding: 4px;
}

/* ── Off-canvas mobile menu ──────────────────────────────── */
.main-menu {
  position: fixed;
  top: 0;
  left: -320px;
  width: 300px;
  height: 100vh;
  background: #fff;
  z-index: 9999;
  overflow-y: auto;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  transition: left 0.35s ease;
  padding-top: 0;
}

/* jQuery custom.js adds .show-menu on hamburger click */
.main-menu:global(.show-menu) {
  left: 0;
}

.mobile-logo-area {
  padding: 14px 20px;
  border-bottom: 1px solid #eee;
}

.mobile-logo-wrap img {
  display: block;
  width: auto;
  max-width: 150px;
  max-height: 52px;
  object-fit: contain;
}

.menu-close-btn {
  cursor: pointer;
  font-size: 24px;
  color: #555;
  padding: 4px;
}

.menu-list {
  list-style: none;
  margin: 0;
  padding: 12px 0;
}

.menu-list li {
  border-bottom: 1px solid #f0f0f0;
}

.menu-list li a {
  display: block;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 500;
  color: #333;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.menu-list li a:hover,
.menu-list li.active a {
  color: var(--primary-color1, #0d6efd);
  background: #f8f9fa;
}

.mobile-menu-auth {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-login-btn,
.menu-signup-btn {
  border: none;
  width: 100%;
  justify-content: center;
}

.menu-signup-btn {
  background: var(--primary-color1, #0d6efd);
}

@media (max-width: 991px) {
  .nav-container {
    height: 56px;
  }

  .nav-brand-logo {
    max-width: 100px;
    max-height: 40px;
  }
}
</style>
